import { withPeriod } from './view-period';
import { DrrValue } from './drr-value';
import { drrHint, drrCaption } from './drr-explanation';
import { useState } from 'react';
import { Modal } from '../ui/modal';
import { Link, useNavigate } from 'react-router';
import type { CampaignRef } from '../api/types';
import { Controls } from './controls';
import { ProductStrip } from './thumb';
import { toneOf, type State, type Tone } from './status';
import { K } from '../kabinet/atoms';
import { Прочерк } from '../ui/nothing';
import { DeferredFeaturePanel as FeaturePanel } from './deferred-details';
import { DeferredBreakdown as Breakdown } from './deferred-details';
import { TopupNow } from './topup-now';
import { IconPlus } from '../ui/icons';
import { МеткаГруппы } from '../pages/gruppy';
import { Спп, ЗапасКапсула } from './zapas';
import { МеткиТестов } from './metki-testov';
import type { Period } from '../pages/period';
import type { Row } from './campaign-table';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * КАМПАНИИ НА ТЕЛЕФОНЕ — КАРТОЧКАМИ, А НЕ ТАБЛИЦЕЙ.
 *
 * До этого файла на 390 показывалась ТА ЖЕ таблица в четырнадцать
 * колонок, что и на большом экране. Четырнадцать колонок в 390 точек
 * не помещаются никак: строка уезжает вбок, и человек листает
 * горизонтально, чтобы прочитать одно число. Первый же столбец при
 * этом уходит за край — то есть не видно, о какой кампании речь.
 *
 * Эталон на 390 рисует ДРУГУЮ РАЗМЕТКУ, а не ту же в другом размере:
 * `probe/design-html.mjs` снимает её отдельным файлом, и сборщик
 * частиц честно говорит «таблицаКампаний: нет на 390».
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЧТО ВЫБРАНО ПОКАЗЫВАТЬ И ПОЧЕМУ ИМЕННО ЭТО.
 *
 * Из четырнадцати колонок в карточке четыре: расход, заказы, ДРР, цена
 * заказа. Так в эталоне, и выбор верный: это ответы на «сколько ушло»,
 * «что пришло», «во сколько обошлось». Остальное — подробности, за ними
 * человек идёт в карточку кампании кнопкой «Подробнее».
 *
 * ПЕРЕКЛЮЧАТЬ КОЛОНКИ ЗДЕСЬ НЕЛЬЗЯ, И ЭТО НЕ УПУЩЕНИЕ. Настройка
 * «Колонки» — про таблицу; в карточке четыре места, и выбор из
 * четырнадцати в них не влезает. Экран телефона отвечает на вопрос
 * «как дела», а не «покажи мне срез».
 * ═══════════════════════════════════════════════════════════════════════
 */

/**
 * Цвет полосы состояния слева.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПОЧЕМУ ЦВЕТ ЗДЕСЬ, А НЕ КЛАССОМ `rk-stripe-*`, КАК У ТАБЛИЦЫ.
 *
 * Частица эталона задаёт полосу целиком — `border-left: 3px solid
 * var(--good)`. Это сокращённая запись, и она перебивает отдельное
 * `border-left-color` из `rk-stripe-*`: у частицы точность выше
 * (`[data-kab] .k-…`), и стоит она в сборке позже.
 *
 * Спорить с частицей значит подправлять собранное из эталона руками —
 * ровно то, ради чего сборщик и заведён. Поэтому форму даёт частица,
 * а цвет приходит сюда значением. Сторож `kabinet/kartochka.spec.ts`
 * держит эти три строки в согласии с `rk-stripe-*`: два места про один
 * цвет обязаны говорить одно.
 * ═══════════════════════════════════════════════════════════════════════
 */
export const ПОЛОСА: Record<Tone, string> = {
  run: 'var(--good)',
  idle: 'var(--warn)',
  dead: 'var(--line-strong)',
};

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПЛАШКА СОСТОЯНИЯ НА КАРТОЧКЕ — ТРЁХ ЦВЕТОВ, А НЕ ОДНОГО ЗЕЛЁНОГО.
 *
 * Частица `k-метка-карточки` вырезана из эталона с РАБОТАЮЩЕЙ кампании
 * и несёт `--good-wash`/`--good-ink` намертво. Надевалась она на все
 * подряд — и «не тратит» с «завершена» выходили ЗЕЛЁНЫМИ, то есть
 * плашка говорила обратное тому, что в ней написано.
 *
 * Сверка это пропускала и не могла не пропустить: она сравнивает
 * первый узел с именем, а он как раз работающий. Третий случай той же
 * породы за день — после чипа отбора и тумблера возможности.
 *
 * ЦВЕТ БЕРЁТСЯ ИЗ ТОГО ЖЕ `toneOf`, ЧТО И ПОЛОСКА СЛЕВА. Требование
 * владельца дословно: «если кнопка оранжевым, значит и полоска тоже
 * оранжевым». Два похожих правила однажды разойдутся; одно не может.
 * ═══════════════════════════════════════════════════════════════════════
 */
export const КРАСКА_МЕТКИ: Record<Tone, React.CSSProperties | undefined> = {
  /* Работающая — зелёная, и это ровно то, что уже стоит в частице:
     подменять нечего. */
  run: undefined,
  idle: { background: 'var(--warn-wash)', color: 'var(--warn-ink)' },
  dead: { background: 'var(--bg-sunken)', color: 'var(--ink-3)' },
};

const деньги = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const целые = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

/**
 * Четыре числа карточки.
 *
 * ПРИЧИНА ПУСТОТЫ У КАЖДОГО СВОЯ — ответ 32. Прочерк без причины
 * читается как поломка, а на телефоне он занимает четверть карточки.
 */
function числа(c: CampaignRef): Array<{ имя: string; знач: number | null; почему: string; тон?: string; подсказка?: string; подпись?: string }> {
  return [
    {
      имя: 'Расход',
      знач: c.spend,
      почему: 'Статистика за этот промежуток ещё не выгружена',
    },
    {
      имя: 'Заказы',
      знач: c.orders,
      почему: 'Статистика за этот промежуток ещё не выгружена',
    },
    {
      имя: 'ДРР',
      подпись: drrCaption(c),
      знач: c.drr,
      подсказка: drrHint(c),
      почему: 'Не из чего посчитать: заказов за промежуток не было, а делить расход не на что',
      тон: 'var(--good-ink)',
    },
    {
      имя: 'Цена заказа',
      знач: c.cpo,
      почему: 'Не из чего посчитать: заказов за промежуток не было',
    },
  ];
}

function печать(имя: string, v: number): string {
  if (имя === 'ДРР') return `${v.toFixed(1).replace('.', ',')} %`;
  if (имя === 'Заказы') return целые.format(Math.round(v));
  if (имя === 'Цена заказа') return `${целые.format(Math.round(v))} ₽`;
  return деньги.format(v);
}

export function CampaignCards({
  rows,
  base,
  wsId,
  cabinetId,
  picked,
  onPick,
  onDone,
  period,
  именаГрупп,
}: {
  rows: Row[];
  base: string;
  wsId: string;
  cabinetId: string;
  picked: Set<string>;
  onPick: (id: string) => void;
  onDone: (text: string) => void;
  period: Period;
  /** Имена групп по опознавателю — для метки в карточке. */
  именаГрупп?: Map<string, string>;
}) {
  return (
    <div className="flex flex-col" data-d="список-карточек-кампаний">
      {rows.map(({ campaign: c, state }) => (
        <Карточка
          key={c.id}
          c={c}
          state={state}
          base={base}
          wsId={wsId}
          cabinetId={cabinetId}
          picked={picked.has(c.id)}
          onPick={() => onPick(c.id)}
          onDone={onDone}
          period={period}
          имяГруппы={c.groupId === null ? undefined : именаГрупп?.get(c.groupId)}
        />
      ))}
    </div>
  );
}

function Карточка({
  c,
  state,
  base,
  wsId,
  cabinetId,
  picked,
  onPick,
  onDone,
  period,
  имяГруппы,
}: {
  c: CampaignRef;
  state: State;
  base: string;
  wsId: string;
  cabinetId: string;
  picked: boolean;
  onPick: () => void;
  onDone: (text: string) => void;
  period: Period;
  имяГруппы?: string;
}) {
  const тон = toneOf(state.health);
  /* РАЗБОР РАСКРЫТ У КАЖДОЙ КАРТОЧКИ СВОЙ: на широком это состояние
     таблицы (множество открытых строк), здесь — состояние карточки.
     Общий список открытых на телефоне не нужен: карточек на экране
     полторы, и раскрыть две сразу человек не пытается. */
  const [открыт, setОткрыт] = useState(false);
  const [деньги, setДеньги] = useState(false);
  const [настройки, setНастройки] = useState(false);
  const navigate = useNavigate();
  const onOpen = () => setОткрыт((б) => !б);

  return (
    <div
      data-d="карточка-кампании"
      className={K.карточкаКампании}
      /* ═══════════════════════════════════════════════════════════════
         ВЫБРАННАЯ КАРТОЧКА — ПОДЛОЖКОЙ ЦВЕТА МАРКИ.

         Так в эталоне: `rowBg: checked ? 'var(--brand-wash)' :
         'transparent'`, и у строки таблицы на широком это давно есть
         (`.rk-row-picked`). На телефоне не было ничего: галочка стоит,
         полоса выделенного внизу считает «1 выделена», а сама карточка
         ничем не отличается от соседних — какую именно выделили, видно
         только по галочке в углу.

         Нашлось не глазом, а СВЕРКОЙ — в тот день, когда состояние
         «выделены строки» вообще подключили к сверке. Оно лежало
         в замере эталона снятым и не читалось ни разу.

         НА МЕСТЕ, А НЕ ЧАСТИЦЕЙ: частицы вырезаются из отрисовок
         эталона, а отрисовки этого состояния нет — стенд рисует
         в файл только экраны. Цвет тот же самый, из той же переменной.
         ═══════════════════════════════════════════════════════════ */
      style={{
        borderLeftColor: ПОЛОСА[тон],
        background: picked ? 'var(--brand-wash)' : undefined,
      }}
    >
      <div className={K.верхКарточки}>
        <input
          type="checkbox"
          style={{ marginTop: 5, flex: '0 0 auto' }}
          checked={picked}
          onChange={onPick}
          aria-label={`Выделить ${c.name ?? c.advertId}`}
        />

        <span className={K.именаКарточки}>
          {/**
           * ИМЯ — ССЫЛКОЙ, А НЕ КНОПКОЙ, ХОТЯ В ЭТАЛОНЕ КНОПКА.
           * Кнопка в прототипе стоит потому, что переходить ему некуда.
           * У нас переход настоящий, и ссылка отличается от кнопки не
           * видом, а тем, что её можно открыть в новой вкладке
           * и переслать.
           */}
          <Link
            to={withPeriod(`/rk/${wsId}/${cabinetId}/campaigns/${c.id}`, period)}
            className={K.имяКарточки}
            title={c.name ?? undefined}
          >
            <b>{c.name ?? `Кампания ${c.advertId}`}</b>
          </Link>

          <span className={K.рядНомера}>
            <span className={K.номерКарточки}>{c.advertId}</span>
            <span
              className={K.меткаКарточки}
              style={КРАСКА_МЕТКИ[тон]}
              title={state.hint ?? undefined}
            >
              {state.text}
            </span>
            {/* МЕТКА ГРУППЫ — ЗДЕСЬ ЖЕ, третьей в ряду. Так в эталоне
                на 390: номер, состояние, группа. Отдельной строкой она
                добавила бы каждой карточке высоты ради одного слова. */}
            {имяГруппы !== undefined && <МеткаГруппы имя={имяГруппы} />}
          </span>
        </span>

        {/* ═══════════════════════════════════════════════════════════
            НА ТЕЛЕФОНЕ ТОЛЬКО ТУМБЛЕР — ЛАМПЫ «ПУСК/ПАУЗА» ЗДЕСЬ НЕТ.

            В эталоне на 390 у карточки один орган: тумблер 44×26.
            Лампа есть в таблице на широком и нет в карточке — значит,
            её здесь и не должно быть («лишнее убери, чего нет
            в дизайне»).

            ЧЕМ ЭТО ПЛАТИТСЯ И ПОЧЕМУ ЭТО НЕ ПОТЕРЯ. Остановить показы
            с телефона из СПИСКА больше нельзя — надо зайти в кампанию
            («Подробнее»), там лампа на месте. Против этого стоит то,
            что на 390 два круглых органа рядом с именем кампании
            различаются одним значком внутри, и промах пальцем между
            ними означает «выключил сервис» вместо «остановил показы».
            Записано вопросом дизайнеру.
            ═══════════════════════════════════════════════════════ */}
        <Controls
          base={base}
          campaign={c}
          state={state}
          onDone={onDone}
          где="карточка"
          withFinish={false}
        />
      </div>

      {/* СКОЛЬКО ТОВАРОВ — СЛОВОМ, А НЕ ТОЛЬКО КАРТИНКАМИ.

          Полоска показывает три миниатюры и «+7»; сколько всего,
          из неё не сложить — «+7» это «ещё семь», а не «десять».
          В эталоне рядом стоит «10 шт», и на телефоне это
          единственное место, где число товаров вообще названо:
          столбца «Товары» здесь нет. Кегль СВОЙ, крупнее табличного
          (`--fs-sm` против `--fs-xs`): на карточке этот счёт
          не подпись под столбцом, а часть строки. */}
      {/* ═══════════════════════════════════════════════════════════
          МЕТКИ ОПЫТОВ — СВОЕЙ СТРОКОЙ, КАК И НА ШИРОКОМ.

          Передача 07.09.2026, раздел 2. На 390 капсула выше — 24 против
          20 на широких: палец попадает по двадцати четырём, по двадцати
          промахивается.
          ═══════════════════════════════════════════════════════════ */}
      <МеткиТестов ставка={c.bidTest} фото={c.photoTest} узкий />

      {c.productCount > 0 && (
        <div className={K.рядФото}>
          <ProductStrip nmIds={c.nmIds ?? []} count={c.productCount} size={34} показать={3} />
          <span className={K.штКарточки}>{c.productCount} шт</span>
          {/* СПП И ОСТАТОК — В ТОЙ ЖЕ СТРОКЕ, ЧТО И ЧИСЛО ТОВАРОВ.
              Так в эталоне на 390: «1 шт · СПП 9 % · ● 37 шт» одной
              строкой. Отдельной строкой они добавили бы каждой карточке
              высоты ради двух капсул. */}
          {/* ПРОПАЖУ ПОЛЯ ПРИВОДИМ К ПУСТОТЕ: `!== null` пропускает
              `undefined`, и капсула роняла экран, читая у него ступень.
              Довод целиком — в `campaign.page.tsx`. */}
          <Спп процент={c.spp} где="строка" когда={c.sppAt} />
          <ЗапасКапсула запас={c.stock} где="строка" короткая />
        </div>
      )}

      <div className={K.сеткаЧисел}>
        {числа(c).map((н) => (
          <span key={н.имя} className={K.числоКарточки} title={н.имя === 'ДРР' ? undefined : н.подсказка}>
            <span className={K.подписьЧисла}>{н.подпись ?? н.имя}</span>
            <b style={{ fontVariantNumeric: 'tabular-nums', color: н.тон }}>
              {н.имя === 'ДРР' ? <DrrValue value={c}>{н.знач === null ? '—' : печать(н.имя, н.знач)}</DrrValue> : (н.знач === null ? <Прочерк почему={н.почему} /> : печать(н.имя, н.знач))}
            </b>
          </span>
        ))}
      </div>

      <button type="button" className="rk-button-quiet" aria-expanded={настройки}
        aria-label={`Настройки кампании «${c.name ?? c.advertId}»`}
        onClick={() => setНастройки(v => !v)}>
        {настройки ? 'Скрыть настройки' : 'Настройки'}
      </button>
      {настройки && <FeaturePanel base={base} campaignId={c.id} campaignName={c.name}
        features={c.features} week={null} managed={c.managed} onDone={onDone}
        onTab={tab => navigate(withPeriod(`/rk/${wsId}/${cabinetId}/campaigns/${c.id}?tab=${tab}`, period))} />}

      <div className={K.рядКнопокКарточки}>
        <Link
          to={withPeriod(`/rk/${wsId}/${cabinetId}/campaigns/${c.id}`, period)}
          className={`${K.кнопкаПодробнее} flex items-center justify-center no-underline`}
        >
          Подробнее
        </Link>
        {/* ═══════════════════════════════════════════════════════════
            «+» — ПОЛОЖИТЬ ДЕНЕГ, ВТОРОЙ КНОПКОЙ В ТОМ ЖЕ РЯДУ.

            Так в эталоне: `Подробнее` тянется, «+» 44×44 стоит справа.
            У нас «Подробнее» занимала всю ширину, и пополнить счёт
            кампании с телефона было нечем — при том, что «кончились
            деньги» это ровно тот повод, по которому в кабинет
            заглядывают с телефона.

            ЗАВЕРШЁННОЙ КЛАСТЬ НЕЛЬЗЯ: деньги на её счёт уже никуда
            не пойдут. Кнопка не прячется, а гаснет — исчезнувшая
            кнопка означала бы, что действие вообще не существует.
            ═══════════════════════════════════════════════════════ */}
        <button
          type="button"
          className={K.кнопкаДенегКарточки}
          disabled={!state.live}
          onClick={() => setДеньги(true)}
          aria-label="Положить денег"
          title={state.live ? 'Положить денег на счёт кампании' : 'Кампания завершена'}
        >
          <IconPlus size={16} />
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          ОКНОМ, А НЕ ВРЕЗКОЙ В КАРТОЧКУ — так в эталоне и так же,
          как на широком.

          Форма пополнения раскрывалась прямо в карточке, отодвигая
          соседние вниз: на телефоне это значит, что человек теряет
          из виду ту самую кампанию, ради которой открыл форму,
          а список под пальцем прыгает. У эталона на этом месте окно
          поверх страницы (`modal:'topup'`), то же самое, что
          из таблицы.
          ═══════════════════════════════════════════════════════ */}
      {деньги && (
        <Modal
          width={520}
          onClose={() => setДеньги(false)}
          имя="окно-пополнения"
          title={<b>Пополнить счёт кампании</b>}
        >
          <TopupNow
            base={base}
            campaignId={c.id}
            title={c.name ?? `Кампания ${c.advertId}`}
            balance={c.balance}
            onClose={() => setДеньги(false)}
            onDone={onDone}
          />
        </Modal>
      )}

      {/* ═══════════════════════════════════════════════════════════
          РАЗБОР ПО ТОВАРАМ — ПОЛОСОЙ ВО ВСЮ КАРТОЧКУ, а не кнопкой
          на границе строки, как на широком.

          Карточка узкая: «2 из 4 приносят заказы, 2 впустую» в кнопку
          на 390 не влезает, а без этих чисел кнопка не зовёт себя
          открыть — «Разбор — 4 товаров» не говорит ничего.

          Так в эталоне: `кнопка-разбора-390`, высота 44 (палец),
          слева название, справа две сводки цветом.
          ═══════════════════════════════════════════════════════ */}
      {c.breakdown.total > 0 && (
        <button
          type="button"
          data-d="кнопка-разбора-390"
          className={K.кнопкаРазбора390}
          onClick={onOpen}
          title="Кто из товаров приносит заказы, а кто тратит"
        >
          <span className={K.левоРазбора390}>
            {/* КАРЕТКА ЗНАКОМ, как в эталоне: `'▾' : '▸'`. */}
            <span className={K.каретаРазбора390}>{открыт ? '▾' : '▸'}</span>
            {/* ИМЯ МЕНЯЕТСЯ ВМЕСТЕ С КАРЕТКОЙ — так в эталоне
                (`openLabelM`). Каретка отвечает на «открыто ли»
                значком, слово — словом; на 44 точках высоты значок
                читается не всеми и не всегда. */}
            <span>{открыт ? 'Свернуть разбор' : 'Разбор по товарам'}</span>
          </span>
          <span className={K.правоРазбора390}>
            <b className={K.хорошоРазбора390}>
              {c.breakdown.works} из {c.breakdown.total} приносят заказы
            </b>
            {c.breakdown.wastes > 0 && (
              <b className={K.плохоРазбора390}>{c.breakdown.wastes} впустую</b>
            )}
          </span>
        </button>
      )}

      {открыт && (
        <Breakdown
          where={{ wsId, cabinetId }}
          base={base}
          campaignId={c.id}
                      campaignMetrics={c}
          period={period}
          open
          onDone={onDone}
        />
      )}
    </div>
  );
}
